/**
 * The single Tina content-type vocabulary.
 *
 * Before this module there were three competing implementations — the run
 * site-picker's `ContentType`/`CONTENT_TYPES`/`CONTENT_TYPE_LABEL`, a private
 * map inside the home spend chart, and a generic snake_case→Title Case
 * transform in `items/item-context.ts` (which rendered "Music Event" where the
 * other two rendered "Music"). Nine `enrichment_*` values then joined them, and
 * adding those to three places is how a vocabulary drifts permanently.
 *
 * Everything else re-exports from here. Import the narrow `SCRAPE_CONTENT_TYPES`
 * wherever the value has to be a *site* content type, and `contentTypeLabel()`
 * wherever a wire value only has to be rendered.
 */

/**
 * Content types a SITE may declare — the pipelines that scrape.
 *
 * Must never gain the `enrichment_*` values: this array feeds the site
 * create/edit form and `POST /sites`, and an enrichment job is not a site.
 */
export const SCRAPE_CONTENT_TYPES = [
  "music_event",
  "cinema_screening",
  "theater_performance",
  // Highlightly leagues/teams/matches (tina `sports_match`). A site type only:
  // tina's `POST /items` rejects it, since sports items have no extract stage.
  "sports_match",
] as const;

export type ScrapeContentType = (typeof SCRAPE_CONTENT_TYPES)[number];

/**
 * The eleven enrichment job content types. Never valid on a
 * site — they are minted by the synthetic `system-enrichment` source, which
 * `GET /sites` excludes by its `system-` prefix rule.
 *
 * `enrichment_play`/`enrichment_performance` (MOO-4814 task 17) sit right
 * after `enrichment_screening`, mirroring theater's cinema twin.
 */
export const ENRICHMENT_CONTENT_TYPES = [
  "enrichment_artist",
  "enrichment_venue",
  "enrichment_event",
  "enrichment_genre",
  "enrichment_show_genre",
  "enrichment_movie",
  "enrichment_person",
  "enrichment_screening",
  "enrichment_play",
  "enrichment_performance",
  "enrichment_geodata",
] as const;

export type EnrichmentContentType = (typeof ENRICHMENT_CONTENT_TYPES)[number];

/**
 * The one event-refresh job content type (moo-4794).
 *
 * Never valid on a site, for the same reason the eleven `enrichment_*` ones are not: a refresh job
 * is an `events` row seeded by the nightly sweep or the duplicate hook against a site that
 * already exists. It stays out of {@link SCRAPE_CONTENT_TYPES}, which feeds `POST /sites`.
 *
 * A plain constant rather than a tuple: unlike enrichment's eleven, there is exactly one, and
 * tina's `ContentType` StrEnum deliberately does **not** gain it either.
 */
export const REFRESH_CONTENT_TYPE = "music_event_refresh";

export type RefreshContentType = typeof REFRESH_CONTENT_TYPE;

/**
 * True for the refresh-job content type.
 *
 * The opt-in that lifts `list_events`' `scrape_class_only()` default, exactly like an
 * `enrichment_*` value: passing `content_type=music_event_refresh` is what makes refresh rows
 * visible on `/tina/items` at all.
 */
export function isRefreshContentType(
  value: string | null | undefined,
): value is RefreshContentType {
  return value === REFRESH_CONTENT_TYPE;
}

/**
 * The eleven ENTITY names — the `enrichment_*` content types with the prefix
 * stripped. This is the vocabulary `POST /enrichments` takes as `entity_type`
 * and `GET /events?entity_id=` scopes by, so it is a wire contract in its own
 * right and mirrors Tina's `EnrichmentEntity` StrEnum (`src/enrichment/types.py`).
 */
export const ENRICHMENT_ENTITIES = [
  "artist",
  "venue",
  "event",
  "genre",
  "show_genre",
  "movie",
  "person",
  "screening",
  "play",
  "performance",
  "geodata",
] as const;

export type EnrichmentEntity = (typeof ENRICHMENT_ENTITIES)[number];

/**
 * The four entities that have an embedding — Tina's `EMBEDDABLE` frozenset,
 * exactly.
 *
 * Load-bearing, not documentation: the Admin API 400s on `embeddingStatus` for
 * the other seven and `POST /enrichments` rejects `mode=embed` for them, so an
 * embedding column or an embed request outside this set is a broken request,
 * not an empty one.
 */
export const EMBEDDABLE_ENTITIES = [
  "artist",
  "venue",
  "genre",
  "event",
] as const;

export type EmbeddableEntity = (typeof EMBEDDABLE_ENTITIES)[number];

export function isEmbeddableEntity(
  entity: string | null | undefined,
): entity is EmbeddableEntity {
  return (EMBEDDABLE_ENTITIES as readonly string[]).includes(entity ?? "");
}

/** `"show_genre"` → `"enrichment_show_genre"`. The inverse of {@link enrichmentEntityOf}. */
export function enrichmentContentTypeOf(
  entity: EnrichmentEntity,
): EnrichmentContentType {
  return `enrichment_${entity}`;
}

export type TinaContentType =
  | ScrapeContentType
  | EnrichmentContentType
  | RefreshContentType;

export const CONTENT_TYPE_LABEL: Record<TinaContentType, string> = {
  music_event: "Music",
  cinema_screening: "Cinema",
  theater_performance: "Theater",
  sports_match: "Sports",
  // Not "Music refresh": the label appears in a content-type column beside
  // "Music", and what distinguishes the row is that it is a refresh of one.
  music_event_refresh: "Refresh",
  enrichment_artist: "Artist enrichment",
  enrichment_venue: "Venue enrichment",
  enrichment_event: "Event enrichment",
  enrichment_genre: "Genre enrichment",
  enrichment_show_genre: "Show-genre enrichment",
  enrichment_movie: "Movie enrichment",
  enrichment_person: "Person enrichment",
  enrichment_screening: "Screening enrichment",
  enrichment_play: "Play enrichment",
  enrichment_performance: "Performance enrichment",
  enrichment_geodata: "Geodata enrichment",
};

/** `{ value, label }` pairs for the scrape types, in picker order. */
export const SCRAPE_CONTENT_TYPE_OPTIONS: ReadonlyArray<{
  value: ScrapeContentType;
  label: string;
}> = [
  { value: "music_event", label: CONTENT_TYPE_LABEL.music_event },
  {
    value: "theater_performance",
    label: CONTENT_TYPE_LABEL.theater_performance,
  },
  { value: "cinema_screening", label: CONTENT_TYPE_LABEL.cinema_screening },
  { value: "sports_match", label: CONTENT_TYPE_LABEL.sports_match },
];

/** `{ value, label }` pairs for the eleven enrichment job types. */
export const ENRICHMENT_CONTENT_TYPE_OPTIONS: ReadonlyArray<{
  value: EnrichmentContentType;
  label: string;
}> = ENRICHMENT_CONTENT_TYPES.map((value) => ({
  value,
  label: CONTENT_TYPE_LABEL[value],
}));

export function isScrapeContentType(
  value: string | null | undefined,
): value is ScrapeContentType {
  return (SCRAPE_CONTENT_TYPES as readonly string[]).includes(value ?? "");
}

export function isEnrichmentContentType(
  value: string | null | undefined,
): value is EnrichmentContentType {
  return (ENRICHMENT_CONTENT_TYPES as readonly string[]).includes(value ?? "");
}

/**
 * `"enrichment_show_genre"` → `"show_genre"`; null for anything that is not an
 * enrichment content type. The suffix is the *entity* the job enriches, which is
 * what the per-entity IMS deep links key on.
 */
export function enrichmentEntityOf(
  value: string | null | undefined,
): EnrichmentEntity | null {
  return isEnrichmentContentType(value)
    ? (value.slice("enrichment_".length) as EnrichmentEntity)
    : null;
}

/** snake_case → Title Case, the pre-Phase-4 fallback for an unmapped value. */
function titleCase(value: string): string {
  return value
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

/**
 * Human label for any `content_type` off the wire, falling back to the
 * snake_case→Title Case transform for a value Tina grew after this deploy.
 * Never throws and never returns an empty string for a non-empty input.
 */
export function contentTypeLabel(value: string | null | undefined): string {
  if (!value) return "—";
  return CONTENT_TYPE_LABEL[value as TinaContentType] ?? titleCase(value);
}
